import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Users,
  Zap,
  Target,
  Activity,
  Megaphone,
  CalendarHeart,
  ClipboardList,
  ShoppingBag,
  AlertTriangle,
  Compass,
  CheckCircle2,
  TrendingUp,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/politics/Header";
import Footer from "@/components/politics/Footer";
import CampaignLeadForm from "@/components/politics/CampaignLeadForm";

/* ============================================================= */
/*  1 — Hero                                                     */
/* ============================================================= */
function CampaignsHero() {
  return (
    <section
      data-testid="campaigns-hero"
      className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28"
    >
      <div className="absolute inset-0 -z-10 rsm-grid-bg opacity-90" />
      <div
        className="absolute -top-32 right-[-12%] -z-10 h-[520px] w-[520px] rounded-full blur-3xl opacity-30"
        style={{
          background: "radial-gradient(closest-side, #FA9F22, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-40 left-[-12%] -z-10 h-[460px] w-[460px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #D94A0A, transparent 70%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8 rsm-rise">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#1B1D1D]/10 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-[#1B1D1D]">
            <Sparkles size={12} className="text-[#FA9F22]" />
            RSM Campanhas · Engagement Media
          </div>
          <h1
            data-testid="campaigns-headline"
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.94] text-[#1B1D1D]"
          >
            Ative milhares de{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#FA9F22]">pessoas reais</span>
              <span className="absolute inset-x-0 bottom-1 h-3 -z-0 bg-[#FA9F22]/20 rounded" />
            </span>{" "}
            para impulsionar suas campanhas.
          </h1>
          <p
            data-testid="campaigns-subhead"
            className="mt-8 max-w-3xl text-lg sm:text-xl leading-relaxed text-[#51565F]"
          >
            A <span className="font-medium text-[#1B1D1D]">RSM Campanhas</span>{" "}
            usa uma rede de agentes para criar Engagement Media: ações
            sincronizadas de pessoas comuns nas redes e no mundo físico.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href="#piloto"
              data-testid="campaigns-hero-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#FA9F22] px-7 py-4 text-base font-semibold text-[#1B1D1D] shadow-sm transition-all duration-300 ease-out hover:bg-[#E68F1A] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#FA9F22] focus:ring-offset-2 focus:ring-offset-[#F5FAFB]"
            >
              Quero desenhar uma campanha com a RSM
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <Link
              to="/politics"
              data-testid="campaigns-hero-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1B1D1D]/15 bg-white/70 backdrop-blur px-7 py-4 text-base font-medium text-[#1B1D1D] hover:border-[#208BFF] hover:text-[#208BFF] transition-all duration-300 ease-out"
            >
              Conhecer RSM Politics
            </Link>
          </div>
          <p
            data-testid="campaigns-microcopy"
            className="mt-6 text-sm text-[#7D8188] max-w-xl"
          >
            Vamos entender seu desafio e montar um piloto sob medida.
          </p>
        </div>

        {/* Side: agents activity visual */}
        <div className="lg:col-span-4 lg:pl-4 rsm-rise rsm-rise-delay-2">
          <div className="rounded-2xl border border-[#1B1D1D]/8 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#7D8188]">
                Campanha · Lançamento X
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-[#FA9F22]/15 px-2 py-0.5 text-[10px] font-semibold text-[#A66400]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#FA9F22] animate-pulse" />
                ATIVA
              </span>
            </div>
            <div className="mt-5 space-y-4">
              <Stat label="Agentes ativados" value="4.612" delta="+12,4%" />
              <Stat label="Ações cumpridas" value="18.301" delta="+27%" />
              <Stat
                label="Engajamento incremental"
                value="3,2×"
                delta="vs. orgânico"
                positive
              />
            </div>
            <div className="mt-6 border-t border-[#1B1D1D]/8 pt-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#7D8188]">
                Ondas de missões
              </span>
              <div className="mt-3 flex items-end gap-1.5 h-16">
                {[28, 54, 72, 88, 96, 78, 62, 84, 70, 92, 86, 74].map(
                  (v, i) => (
                    <div
                      key={`bar-${v}`}
                      className="flex-1 rounded-sm bg-gradient-to-t from-[#FA9F22]/20 to-[#FA9F22]"
                      style={{ height: `${v}%` }}
                    />
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, delta, positive = true }) {
  return (
    <div className="flex items-end justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#7D8188] font-semibold">
          {label}
        </div>
        <div className="font-display text-2xl font-bold tracking-tight text-[#1B1D1D] mt-1">
          {value}
        </div>
      </div>
      <span
        className={`text-xs font-semibold ${
          positive ? "text-[#A66400]" : "text-[#D94A0A]"
        }`}
      >
        {delta}
      </span>
    </div>
  );
}

/* ============================================================= */
/*  2 — Why                                                       */
/* ============================================================= */
function WhySection() {
  const blocks = [
    {
      icon: TrendingUp,
      eyebrow: "Situação",
      title: "Mídia paga ficou cara — e a atenção, mais escassa.",
      body: "CPMs sobem todo trimestre. Posts orgânicos têm alcance limitado. Influenciadores grandes trazem volume, mas frequentemente baixa profundidade ou autenticidade.",
      accent: "#FA9F22",
    },
    {
      icon: AlertTriangle,
      eyebrow: "Problema",
      title: "Campanhas geram picos artificiais — não comunidade.",
      body: "Marcas investem alto, ativam mídia e influência, e veem o resultado evaporar quando a verba acaba. Falta um meio do caminho com gente real, sincera e medida.",
      accent: "#D94A0A",
    },
    {
      icon: Target,
      eyebrow: "Implicação",
      title: "Sem essa terceira camada, o ROI fica refém do impulso pago.",
      body: "Sem pessoas reais falando da marca de forma coordenada, cada campanha começa do zero. O algoritmo, o varejo e a opinião pública não recebem o sinal social que valida o investimento.",
      accent: "#1B1D1D",
    },
  ];

  return (
    <section
      id="por-que"
      data-testid="campaigns-why"
      className="relative py-24 sm:py-32 border-t border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#FA9F22]">
            Por que Engagement Media
          </span>
          <h2
            data-testid="campaigns-why-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Mídia paga + influenciador não bastam mais.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-2xl">
            Marcas que mais crescem em 2026 não estão substituindo ads e
            influenciadores — estão adicionando uma terceira camada de gente
            real, coordenada e mensurável.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {blocks.map((b, i) => {
            const Icon = b.icon;
            return (
              <article
                key={b.eyebrow}
                data-testid={`campaigns-why-card-${i}`}
                className="group relative flex flex-col rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md"
              >
                <div
                  className="grid h-11 w-11 place-items-center rounded-xl"
                  style={{
                    backgroundColor: `${b.accent}15`,
                    color: b.accent,
                  }}
                >
                  <Icon size={22} />
                </div>
                <span
                  className="mt-5 text-[10px] font-bold tracking-[0.22em] uppercase"
                  style={{ color: b.accent }}
                >
                  {b.eyebrow}
                </span>
                <h3 className="mt-2 font-display text-xl font-semibold leading-tight text-[#1B1D1D]">
                  {b.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {b.body}
                </p>
              </article>
            );
          })}
        </div>

        <div className="mt-12 rounded-2xl border border-[#FA9F22]/25 bg-gradient-to-br from-white to-[#FA9F22]/8 p-8 sm:p-10">
          <p
            data-testid="campaigns-why-closing"
            className="font-display text-2xl sm:text-3xl tracking-tight text-[#1B1D1D] leading-tight max-w-4xl"
          >
            Marcas precisam de uma terceira camada:{" "}
            <span className="text-[#FA9F22]">
              pessoas comuns falando da marca de forma coordenada
            </span>
            , medível e alinhada ao plano de campanha.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  3 — How — Engagement Media pillars                            */
/* ============================================================= */
const PILLARS = [
  {
    icon: Users,
    title: "Rede de micro-agentes",
    desc: "Pessoas comuns conectadas à marca por perfil, região ou interesse — não influenciadores, não bots. Gente real, com histórico de desempenho.",
    accent: "#FA9F22",
  },
  {
    icon: Zap,
    title: "Ações digitais e presenciais",
    desc: "Curtidas, comentários, compartilhamentos, enquetes, reviews, presença em eventos, flashmobs comerciais, cliente oculto — tudo orquestrado.",
    accent: "#208BFF",
  },
  {
    icon: Target,
    title: "Missões coordenadas",
    desc: "Ações não são aleatórias. Seguem um plano de missões desenhado com o cliente, por ondas, segmentos e objetivos.",
    accent: "#7E20FF",
  },
  {
    icon: Activity,
    title: "Medição em tempo real",
    desc: "Painéis com agentes ativados, ações realizadas e sinais qualitativos e quantitativos. ROI sai do achismo e vira indicador.",
    accent: "#3ED383",
  },
];

function HowSection() {
  return (
    <section
      id="engagement-media"
      data-testid="campaigns-how"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#FA9F22]">
            O que é Engagement Media
          </span>
          <h2
            data-testid="campaigns-how-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Quatro engrenagens que transformam pessoas comuns em mídia ativa.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <article
                key={pillar.title}
                data-testid={`campaigns-how-card-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15"
              >
                <div
                  className="grid h-11 w-11 place-items-center rounded-xl"
                  style={{
                    backgroundColor: `${pillar.accent}15`,
                    color: pillar.accent,
                  }}
                >
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
                  {pillar.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {pillar.desc}
                </p>
                <span
                  className="absolute -bottom-12 -right-12 h-32 w-32 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-15"
                  style={{ background: pillar.accent }}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  4 — Use cases                                                 */
/* ============================================================= */
const USE_CASES = [
  {
    icon: Megaphone,
    title: "Lançamento de produto / promoção",
    desc: "Agentes ativando redes sociais com conteúdos, participando de desafios e gerando UGC autêntico em ondas coordenadas com a campanha paga.",
    accent: "#FA9F22",
  },
  {
    icon: CalendarHeart,
    title: "Eventos e ativações presenciais",
    desc: "Recrutamento de agentes para encher um evento, flashmobs comerciais, conteúdo local e geração de buzz no entorno físico da marca.",
    accent: "#7E20FF",
  },
  {
    icon: ClipboardList,
    title: "Pesquisa e feedback contínuo",
    desc: "Agentes respondendo surveys, espalhando enquetes e trazendo impressões da ponta — pesquisa que vive entre os ciclos formais.",
    accent: "#208BFF",
  },
  {
    icon: ShoppingBag,
    title: "Cliente oculto / auditoria",
    desc: "Agentes visitando lojas, testando serviços, validando experiência do cliente e reportando em missões estruturadas com fotos e notas.",
    accent: "#3ED383",
  },
];

function UseCasesSection() {
  return (
    <section
      id="usos"
      data-testid="campaigns-usecases"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Como marcas estão usando
          </span>
          <h2
            data-testid="campaigns-usecases-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Quatro cenários onde a rede entrega valor desproporcional.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          {USE_CASES.map((u, i) => {
            const Icon = u.icon;
            return (
              <article
                key={u.title}
                data-testid={`campaigns-usecase-${i}`}
                className="group relative flex flex-col rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15"
              >
                <div className="flex items-start gap-5">
                  <div
                    className="grid h-12 w-12 place-items-center rounded-xl flex-shrink-0"
                    style={{
                      backgroundColor: `${u.accent}15`,
                      color: u.accent,
                    }}
                  >
                    <Icon size={22} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display text-xl font-semibold text-[#1B1D1D]">
                      {u.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                      {u.desc}
                    </p>
                  </div>
                </div>
                <div
                  className="mt-6 h-1 w-10 rounded-full transition-all duration-300 group-hover:w-20"
                  style={{ background: u.accent }}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  5 — Process                                                   */
/* ============================================================= */
const STEPS = [
  {
    n: "01",
    title: "Briefing e objetivo",
    desc: "Entendemos o desafio: aumentar awareness de lançamento, ativar Black Friday, testar conceito, fazer cliente oculto, etc.",
  },
  {
    n: "02",
    title: "Desenho das missões e segmentação",
    desc: "Co-criação dos tipos de missão e perfis de agentes ideais. Ondas, segmentos, métricas combinadas no papel.",
  },
  {
    n: "03",
    title: "Execução com a base RSM",
    desc: "RSM distribui as missões, acompanha execução em tempo real e garante qualidade mínima por agente.",
  },
  {
    n: "04",
    title: "Relatórios e aprendizados",
    desc: "Entregamos resultados quantitativos, qualitativos e insights para ajustar a próxima onda — ou a próxima campanha.",
  },
];

function ProcessSection() {
  return (
    <section
      id="processo"
      data-testid="campaigns-process"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Como a campanha é construída
          </span>
          <h2
            data-testid="campaigns-process-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Processo consultivo em quatro movimentos.
          </h2>
        </div>

        <ol className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((step, i) => (
            <li
              key={step.n}
              data-testid={`campaigns-process-step-${i}`}
              className="relative rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#FA9F22]/40"
            >
              <span className="font-mono text-sm font-semibold text-[#FA9F22]">
                {step.n}
              </span>
              <h3 className="mt-3 font-display text-xl font-semibold text-[#1B1D1D]">
                {step.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#51565F]">
                {step.desc}
              </p>
              {i < STEPS.length - 1 && (
                <span className="hidden lg:block absolute top-1/2 right-[-14px] -translate-y-1/2 text-[#1B1D1D]/15">
                  <ArrowRight size={20} />
                </span>
              )}
            </li>
          ))}
        </ol>

        <div className="mt-14 text-center">
          <a
            href="#piloto"
            data-testid="campaigns-process-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FA9F22] px-7 py-4 text-base font-semibold text-[#1B1D1D] transition-all duration-300 ease-out hover:bg-[#E68F1A] hover:-translate-y-0.5"
          >
            Quero discutir uma campanha piloto
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  6 — Status / call to cases                                    */
/* ============================================================= */
function StatusSection() {
  return (
    <section
      data-testid="campaigns-status"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden border-y border-[#1B1D1D]/8"
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #F5FAFB 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />
      <div
        className="absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #FA9F22, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-[#FA9F22]">
            <Compass size={14} /> Estado atual
          </span>
          <h2
            data-testid="campaigns-status-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.98] text-[#F5FAFB]"
          >
            Em construção dos primeiros cases — com marcas selecionadas.
          </h2>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-[#F5FAFB]/75">
            RSM Campanhas está em fase de validação do modelo. Estamos abrindo
            poucos pilotos com empresas que tenham desafios interessantes em{" "}
            <span className="text-[#F5FAFB] font-medium">
              lançamento, eventos, pesquisa de mercado e auditoria de
              experiência
            </span>
            . Quem entra agora ajuda a desenhar o padrão — e vira referência no
            mercado.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-7">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#FA9F22]/15 text-[#FA9F22]">
              <Sparkles size={22} />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold text-[#F5FAFB]">
              O que isso significa para você
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-[#F5FAFB]/75">
              {[
                "Atenção dedicada do time RSM no desenho do piloto.",
                "Co-criação real — sua campanha não cabe em template.",
                "Tarifa especial de cases para os primeiros parceiros.",
              ].map((t, i) => (
                <li key={t} className="flex items-start gap-2.5">
                  <CheckCircle2
                    size={15}
                    className="mt-0.5 text-[#FA9F22] flex-shrink-0"
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  7 — FAQ                                                       */
/* ============================================================= */
const FAQS = [
  {
    q: "Que tipo de empresa se beneficia mais de RSM Campanhas?",
    a: "Marcas de consumo, varejistas e redes de loja, organizadores de eventos/festivais, lançamentos de produto e empresas que já investem em mídia paga e influência mas querem uma camada complementar de gente real coordenada.",
  },
  {
    q: "Qual é a diferença para contratar influenciadores tradicionais?",
    a: "Influenciadores entregam audiência concentrada em poucas pessoas. RSM entrega audiência distribuída em milhares de agentes comuns, coordenados em missões com objetivos e métricas claras. São complementares — e o sinal social que muitos agentes geram dificilmente é replicado por poucos perfis grandes.",
  },
  {
    q: "Como funciona a remuneração dos agentes?",
    a: "Modelo de pontos / créditos. O cliente define a campanha, as ações e os incentivos; a RSM cuida da mecânica de distribuição, validação das ações e remuneração da base. Você não negocia com cada agente individualmente.",
  },
  {
    q: "Qual o tamanho mínimo de campanha?",
    a: "Pilotos começam tipicamente a partir de algumas centenas de agentes e um escopo de 4 a 8 semanas. O dimensionamento exato é fechado no briefing — depende do objetivo, da região e do tipo de missões.",
  },
  {
    q: "Como medimos o sucesso de uma campanha RSM?",
    a: "Combinamos indicadores quantitativos (ações realizadas, alcance, engajamento incremental, conversão em pontos de venda quando aplicável) com qualitativos (conteúdo gerado, feedback estruturado dos agentes, sinais de mercado). O painel acompanha tudo em tempo real.",
  },
];

function CampaignsFAQ() {
  return (
    <section
      id="faq"
      data-testid="campaigns-faq"
      className="relative py-24 sm:py-32 bg-white border-t border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-4xl px-6">
        <div className="text-center">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#FA9F22]">
            Perguntas frequentes
          </span>
          <h2
            data-testid="campaigns-faq-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            As dúvidas mais comuns de quem está avaliando o primeiro piloto.
          </h2>
        </div>

        <Accordion
          type="single"
          collapsible
          className="mt-12 divide-y divide-[#1B1D1D]/10 rounded-2xl border border-[#1B1D1D]/10 bg-[#F5FAFB]"
          data-testid="campaigns-faq-accordion"
        >
          {FAQS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-b-0 px-6"
              data-testid={`campaigns-faq-item-${i}`}
            >
              <AccordionTrigger className="text-left font-display text-lg font-semibold text-[#1B1D1D] hover:text-[#FA9F22] hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] leading-relaxed text-[#51565F] pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  Page                                                          */
/* ============================================================= */
export default function CampaignsPage() {
  return (
    <main
      data-testid="campaigns-page"
      className="min-h-screen bg-[#F5FAFB] text-[#1B1D1D]"
    >
      <Header />
      <CampaignsHero />
      <WhySection />
      <HowSection />
      <UseCasesSection />
      <ProcessSection />
      <StatusSection />
      <CampaignsFAQ />
      <CampaignLeadForm />
      <Footer />
    </main>
  );
}
